import React, { useState } from 'react';
import {
  Drawer,
  Button,
  Space,
  List,
  Empty,
  Spin,
  Alert,
  Typography,
  Popconfirm,
  App
} from 'antd';
import {
  DownloadOutlined,
  DeleteOutlined,
  ExpandOutlined,
  FileOutlined,
  LinkOutlined,
  FileUnknownOutlined
} from '@ant-design/icons';
import PDFViewerCore from '../common/PDFViewerCore';
import OfficeDocumentViewer from '../common/OfficeDocumentViewer';
import PPTXViewer from '../common/PPTXViewer';

const { Text } = Typography;

const FileViewerDrawer = ({
  file,
  projectId,
  allFiles,
  open,
  onClose,
  onFileSwitch,
  onDelete
}) => {
  const { message } = App.useApp();
  const [loading, setLoading] = useState(true);
  const [iframeError, setIframeError] = useState(false);

  // Get file type
  const getFileType = (file) => {
    if (!file?.filename) return null;
    return file.filename.split('.').pop().toLowerCase();
  };

  // Check if URL is localhost (local development)
  const isLocalUrl = (url) => {
    return url && (url.includes('localhost') || url.includes('127.0.0.1'));
  };

  // Get appropriate viewer URL for non-PDF files
  const getViewerUrl = (file) => {
    if (!file?.url) {
      console.log('FileViewerDrawer: No file URL', file);
      return null;
    }

    const fileType = getFileType(file);
    const isLocal = isLocalUrl(file.url);

    console.log('FileViewerDrawer: File type:', fileType, 'URL:', file.url, 'Is Local:', isLocal);

    // Use PDFViewerCore for PDFs, iframe viewers for Office docs
    switch (fileType) {
      case 'pdf':
        // Will be handled by PDFViewerCore, return null
        return null;

      case 'ppt':
      case 'pptx':
      case 'doc':
      case 'docx':
      case 'xls':
      case 'xlsx':
        // Microsoft Office Viewer only works with public URLs
        if (isLocal) {
          console.log('FileViewerDrawer: Local URL detected, Office viewer not available');
          return null; // Will show download message for local files
        }

        {
        const fileUrl = encodeURIComponent(file.url);
        const viewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${fileUrl}`;
        console.log('FileViewerDrawer: Generated viewer URL:', viewerUrl);
        return viewerUrl;
        }

      default:
        // Unsupported file type
        console.log('FileViewerDrawer: Unsupported file type:', fileType);
        return null;
    }
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Handle download
  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = file.url;
    link.download = file.filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    message.success(`Downloading ${file.filename}`);
  };

  // Handle delete
  const handleDelete = async () => {
    await onDelete(file._id, file.filename);
    onClose();
  };

  // Handle copy link
  const handleCopyLink = () => {
    navigator.clipboard.writeText(file.url);
    message.success('Link copied to clipboard');
  };

  // Handle full screen view
  const handleFullScreen = () => {
    // Open in new tab for full-screen experience
    window.open(file.url, '_blank');
  };

  // Handle file switch
  const handleFileSwitchInternal = (newFile) => {
    setLoading(true);
    setIframeError(false);
    onFileSwitch(newFile);
  };

  // Handle iframe load
  const handleIframeLoad = () => {
    setLoading(false);
  };

  // Handle iframe error
  const handleIframeError = () => {
    setLoading(false);
    setIframeError(true);
  };

  const viewerUrl = getViewerUrl(file);
  const fileType = getFileType(file);
  const fileTypeUpper = fileType ? fileType.toUpperCase() : 'UNKNOWN';
  const isPDF = fileType === 'pdf';
  const isPPTX = fileType === 'pptx' || fileType === 'ppt';
  const isOfficeDoc = ['docx', 'doc', 'xlsx', 'xls'].includes(fileType);
  const isLargeFile = file.file_size > 10_000_000; // 10MB

  return (
    <Drawer
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingRight: 40 }}>
          <div>
            <Text strong>{file.filename}</Text>
            <br />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {fileTypeUpper} • {formatFileSize(file.file_size)}
            </Text>
          </div>
        </div>
      }
      placement="right"
      width="70%"
      onClose={onClose}
      open={open}
      extra={
        <Space>
          <Button
            icon={<DownloadOutlined />}
            onClick={handleDownload}
          >
            Download
          </Button>
          <Button
            icon={<LinkOutlined />}
            onClick={handleCopyLink}
          >
            Copy Link
          </Button>
          {onDelete && (
            <Popconfirm
              title="Delete File"
              description={`Delete "${file.filename}"?`}
              onConfirm={handleDelete}
              okText="Yes"
              cancelText="No"
              okButtonProps={{ danger: true }}
            >
              <Button
                icon={<DeleteOutlined />}
                danger
              >
                Delete
              </Button>
            </Popconfirm>
          )}
          <Button
            icon={<ExpandOutlined />}
            onClick={handleFullScreen}
          >
            Full View
          </Button>
        </Space>
      }
      styles={{
        body: { padding: 0, height: '100%' }
      }}
    >
      <div style={{ display: 'flex', height: '100%' }}>
        {/* Left Sidebar: File List */}
        <div
          style={{
            width: 220,
            borderRight: '1px solid var(--rlc-surface-muted)',
            overflowY: 'auto',
            background: 'var(--rlc-surface-subtle)'
          }}
        >
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--rlc-surface-muted)' }}>
            <Text strong>Documents ({allFiles.length})</Text>
          </div>
          <List
            dataSource={allFiles}
            renderItem={(item) => (
              <List.Item
                style={{
                  cursor: 'pointer',
                  background: item._id === file._id ? 'var(--rlc-surface-accent)' : 'transparent',
                  padding: '8px 16px',
                  borderLeft: item._id === file._id ? '3px solid var(--rlc-state-info)' : '3px solid transparent'
                }}
                onClick={() => handleFileSwitchInternal(item)}
              >
                <Space style={{ width: '100%' }}>
                  <FileOutlined style={{ color: item._id === file._id ? 'var(--rlc-state-info)' : 'var(--rlc-text-muted)' }} />
                  <div style={{ flex: 1, overflow: 'hidden' }}>
                    <Text
                      ellipsis
                      style={{
                        fontSize: 13,
                        fontWeight: item._id === file._id ? 600 : 400
                      }}
                    >
                      {item.filename}
                    </Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      {formatFileSize(item.file_size)}
                    </Text>
                  </div>
                </Space>
              </List.Item>
            )}
          />
        </div>

        {/* Right Panel: File Preview */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          {/* Warning for large files */}
          {isLargeFile && (
            <div style={{ padding: 16 }}>
              <Alert
                message="Large file detected"
                description="Preview may take a moment to load. Consider downloading for faster access."
                type="info"
                showIcon
                closable
              />
            </div>
          )}

          {/* File Preview Area */}
          <div style={{ flex: 1, position: 'relative', background: 'var(--rlc-surface)' }}>
            {isPDF ? (
              // Use PDFViewerCore for PDF files
              <PDFViewerCore
                pdfUrl={file.url}
                filename={file.filename}
                showToolbar={true}
                toolbarPosition="top"
                onDownload={handleDownload}
                containerStyle={{ height: '100%' }}
              />
            ) : isPPTX ? (
              // Use PPTXViewer for PowerPoint files
              <PPTXViewer
                documentId={file._id || file.id}
                filename={file.filename}
                onDownload={handleDownload}
                containerStyle={{ height: '100%' }}
                showToolbar={true}
                toolbarPosition="top"
              />
            ) : isOfficeDoc ? (
              // Use OfficeDocumentViewer for DOCX and XLSX files
              <OfficeDocumentViewer
                fileUrl={file.url}
                filename={file.filename}
                fileType={fileType}
                onDownload={handleDownload}
                containerStyle={{ height: '100%' }}
              />
            ) : viewerUrl ? (
              // Use iframe viewer for Office documents
              <>
                {loading && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      zIndex: 10
                    }}
                  >
                    <Spin size="large" tip="Loading preview..." />
                  </div>
                )}

                {iframeError ? (
                  <div style={{ padding: 40, textAlign: 'center' }}>
                    <Empty
                      description="Failed to load preview"
                      extra={
                        <Space direction="vertical">
                          <Text type="secondary">
                            The file viewer encountered an error. This may happen if the file is not publicly accessible.
                          </Text>
                          <Button
                            type="primary"
                            icon={<DownloadOutlined />}
                            onClick={handleDownload}
                          >
                            Download to View
                          </Button>
                        </Space>
                      }
                    />
                  </div>
                ) : (
                  <iframe
                    src={viewerUrl}
                    width="100%"
                    height="100%"
                    style={{
                      border: 'none',
                      display: loading ? 'none' : 'block'
                    }}
                    title={file.filename}
                    onLoad={handleIframeLoad}
                    onError={handleIframeError}
                  />
                )}
              </>
            ) : (
              // Unsupported file type (mainly PPTX and other formats)
              <div style={{ padding: 40, textAlign: 'center' }}>
                <Empty
                  image={<FileOutlined style={{ fontSize: 64, color: 'var(--rlc-state-info)' }} />}
                  description={
                    <Space direction="vertical" size="large">
                      <div>
                        <Text strong style={{ fontSize: 16 }}>
                          {fileType === 'pptx' || fileType === 'ppt'
                            ? 'PowerPoint Preview - Download Required'
                            : `Cannot preview .${fileTypeUpper} files`}
                        </Text>
                      </div>
                      <Text type="secondary" style={{ maxWidth: 500, display: 'block' }}>
                        {fileType === 'pptx' || fileType === 'ppt'
                          ? 'PowerPoint files cannot be previewed in the browser. Please download the file to view it in Microsoft PowerPoint or another compatible application.'
                          : 'This file type is not supported for preview. Please download to view.'}
                      </Text>
                      <Alert
                        message="Supported Previews"
                        description="PDF (full viewer), Word documents (DOCX), and Excel spreadsheets (XLSX) can be previewed directly in the browser."
                        type="info"
                        showIcon
                        style={{ maxWidth: 500, margin: '0 auto', textAlign: 'left' }}
                      />
                    </Space>
                  }
                  extra={
                    <Space direction="vertical" size="middle" style={{ marginTop: 20 }}>
                      <Button
                        type="primary"
                        icon={<DownloadOutlined />}
                        onClick={handleDownload}
                        size="large"
                      >
                        Download File
                      </Button>
                      <Text type="secondary">
                        {file.filename} • {formatFileSize(file.file_size)}
                      </Text>
                    </Space>
                  }
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </Drawer>
  );
};

export default FileViewerDrawer;
